import { MY_ACCOUNT_BASE_URL } from "@/constants";
import type { BannerContent } from "./global-banner.types";

/**
 * Gets the appropriate banner content based on current local time and pathname
 * @param now - Current date (defaults to new Date() for easier testing)
 * @param pathname - Current page pathname (optional, used for page-specific banners)
 * @returns Banner content configuration or null if no active promotion
 */
export function getBannerContent(
  now: Date = new Date(),
  pathname?: string,
): BannerContent | null {
  const year = now.getFullYear();
  const currentTime = now.getTime();

  const isInRange = (
    startMonth: number,
    startDay: number,
    startHour: number,
    startMinute: number,
    endMonth: number,
    endDay: number,
    endHour: number,
    endMinute: number,
  ) => {
    const start = new Date(year, startMonth, startDay, startHour, startMinute);
    const end = new Date(year, endMonth, endDay, endHour, endMinute, 59);

    return currentTime >= start.getTime() && currentTime <= end.getTime();
  };

  const isWishlistPage = pathname?.includes("/wishlist");

  // Hide banner on discover sub-pages to avoid content overlap with nav
  // Pattern: /gb/topshop/discover/denim/loose, /gb/topman/discover/denim/skinny, etc.
  const isDiscoverSubPage =
    /^\/(gb\/)?(topshop|topman)\/discover\/[^/]+\/.+/.test(pathname ?? "");
  if (isDiscoverSubPage) {
    return null;
  }

  // Wishlist Perks: 19th Jan 00:00 - 19th Feb 23:59 (wishlist page only)
  const isWishlistPerksPeriod = isInRange(0, 19, 0, 0, 1, 19, 23, 59);

  // Tolu Coker Collab (sign up): 19th Feb 17:00 - 1st Mar 23:59
  const isToluCokerCollabPeriod = isInRange(1, 19, 17, 0, 2, 1, 23, 59);

  // Tolu Coker x Topshop (shop now): 2nd Mar 00:00 - 4th Mar 23:59
  const isToluCokerShopPeriod = isInRange(2, 2, 0, 0, 2, 4, 23, 59);

  // New Arrivals: 5th Mar 00:00 - 8th Mar 23:59
  const isNewArrivalsPeriod = isInRange(2, 5, 0, 0, 2, 8, 23, 59);

  // Wishlist-specific banner takes priority on wishlist page
  if (isWishlistPage && isWishlistPerksPeriod) {
    return {
      title: "",
      subtitle:
        "ORDERS OVER £40 - FREE STANDARD DELIVERY (NEXT DAY DELIVERY ONLY £2.95)",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: false,
    };
  }

  if (isNewArrivalsPeriod) {
    return {
      title: "",
      subtitle:
        "NEW ARRIVALS: FROM BEST OCCASIONWEAR DRESSES TO TRENDING DENIM - SHOP THE LATEST LOOKS HERE",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: true,
      href: "/topshop/category/new-in?cid=52770",
    };
  }

  if (isToluCokerShopPeriod) {
    return {
      title: "",
      subtitle: [
        { type: "text", value: "Tolu Coker x Topshop: " },
        {
          type: "link",
          label: "shop",
          href: "/topshop/category/ctas/topshop-edit-4?cid=52990",
        },
        { type: "text", value: " the collection now & find out " },
        {
          type: "link",
          label: "more",
          href: "/topshop/editorial/tolu-coker",
        },
        { type: "text", value: " about the exclusive collaboration" },
      ],
      text: "",
      code: "",
      colorScheme: "black",
      clickable: false,
    };
  }

  if (isToluCokerCollabPeriod) {
    return {
      title: "TOLU COKER X TOPSHOP",
      subtitle: "Sign up to be the first in line (02.03.2026)",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: true,
      href: MY_ACCOUNT_BASE_URL,
    };
  }

  return null;
}
